import {
  ImportHeaderStatusName,
  ImportDetailStatusName,
  ImportStatusId,
} from '../interfaces/Import/ShipmentBulkUploadApi'
import {
  clientNameError,
  clientNameWordCountError,
  consigneePhoneError,
} from './shipmentValidation'

/**
 * Mapeo de estados de carga masiva a etiquetas (es) y clases de badge (Tailwind),
 * más mapeo de códigos de error a mensajes UX y clasificación transitorio/dato.
 *
 * Calibrado a DESIGN.md (tokens semánticos):
 *   InProcess → main (azul env-aware) · Finalized → success #16a34a
 *   FinalizedWithErrors → warning #f59e0b · Cancelled → gray · Deleted → error #dc2626
 *   Executed (detalle) → success · Error → error · Pending → gray
 *
 * Resolución por `status_name` (estable); el `status_id` numérico (68–75) es solo fallback,
 * porque los IDs son placeholders del backend y pueden colisionar con [config].[Status].
 */

export type StatusTone = 'info' | 'success' | 'warning' | 'error' | 'neutral'

export interface StatusMeta {
  label: string
  /** Clases Tailwind para el badge (pill). */
  badgeClass: string
  tone: StatusTone
}

const HEADER_META: Record<string, StatusMeta> = {
  [ImportHeaderStatusName.InProcess]: {
    label: 'En proceso',
    badgeClass: 'bg-main-50 text-main-600',
    tone: 'info',
  },
  [ImportHeaderStatusName.Finalized]: {
    label: 'Finalizado',
    badgeClass: 'bg-green-50 text-green-700',
    tone: 'success',
  },
  [ImportHeaderStatusName.FinalizedWithErrors]: {
    label: 'Finalizado con errores',
    badgeClass: 'bg-amber-50 text-amber-700',
    tone: 'warning',
  },
  [ImportHeaderStatusName.Cancelled]: {
    label: 'Cancelado',
    badgeClass: 'bg-gray-100 text-gray-600',
    tone: 'neutral',
  },
  [ImportHeaderStatusName.Deleted]: {
    label: 'Eliminado',
    badgeClass: 'bg-red-50 text-red-700',
    tone: 'error',
  },
}

const DETAIL_META: Record<string, StatusMeta> = {
  [ImportDetailStatusName.Pending]: {
    label: 'Pendiente',
    badgeClass: 'bg-gray-100 text-gray-600',
    tone: 'neutral',
  },
  [ImportDetailStatusName.Executing]: {
    label: 'En ejecución',
    badgeClass: 'bg-main-50 text-main-600',
    tone: 'info',
  },
  [ImportDetailStatusName.Executed]: {
    label: 'OK',
    badgeClass: 'bg-green-50 text-green-700',
    tone: 'success',
  },
  [ImportDetailStatusName.Error]: {
    label: 'Error',
    badgeClass: 'bg-red-50 text-red-700',
    tone: 'error',
  },
  [ImportDetailStatusName.Substituted]: {
    label: 'Sustituida',
    badgeClass: 'bg-gray-100 text-gray-600',
    tone: 'neutral',
  },
  [ImportDetailStatusName.ErrorProcessing]: {
    label: 'Error procesando',
    badgeClass: 'bg-red-50 text-red-700',
    tone: 'error',
  },
  [ImportDetailStatusName.ErrorServer]: {
    label: 'Error servidor',
    badgeClass: 'bg-amber-50 text-amber-700',
    tone: 'warning',
  },
}

/** Fallback por ID numérico → nombre interno (si el backend no manda status_name). */
const HEADER_ID_TO_NAME: Record<number, string> = {
  [ImportStatusId.HeaderInProcess]: ImportHeaderStatusName.InProcess,
  [ImportStatusId.HeaderFinalized]: ImportHeaderStatusName.Finalized,
  [ImportStatusId.HeaderFinalizedWithErrors]:
    ImportHeaderStatusName.FinalizedWithErrors,
  [ImportStatusId.HeaderCancelled]: ImportHeaderStatusName.Cancelled,
  [ImportStatusId.HeaderDeleted]: ImportHeaderStatusName.Deleted,
}

const DETAIL_ID_TO_NAME: Record<number, string> = {
  [ImportStatusId.DetailPending]: ImportDetailStatusName.Pending,
  [ImportStatusId.DetailExecuting]: ImportDetailStatusName.Executing,
  [ImportStatusId.DetailExecuted]: ImportDetailStatusName.Executed,
  [ImportStatusId.DetailError]: ImportDetailStatusName.Error,
  [ImportStatusId.DetailSubstituted]: ImportDetailStatusName.Substituted,
  [ImportStatusId.DetailErrorProcessing]:
    ImportDetailStatusName.ErrorProcessing,
  [ImportStatusId.DetailErrorServer]: ImportDetailStatusName.ErrorServer,
}

// Decisión de producto: un estatus que el front no supo decodificar se muestra como
// 'Pendiente', no como 'Desconocido' — para el operador la carga recién entrada todavía
// no pasó por nada, y "Desconocido" se leía como error.
// El costo asumido: colisiona con el 'Pendiente' real de DETAIL_META, así que este label
// no distingue "pendiente de verdad" de "no lo pude decodificar", ni en pantalla ni en el
// Excel que exporta el listado. Si alguna vez hace falta separarlos, el lugar es acá.
const UNKNOWN_META: StatusMeta = {
  label: 'Pendiente',
  badgeClass: 'bg-gray-100 text-gray-600',
  tone: 'neutral',
}

export function headerStatusMeta(
  statusName?: string | null,
  statusId?: number | null,
): StatusMeta {
  if (statusName && HEADER_META[statusName]) return HEADER_META[statusName]
  if (statusId != null && HEADER_ID_TO_NAME[statusId]) {
    return HEADER_META[HEADER_ID_TO_NAME[statusId]]
  }
  return UNKNOWN_META
}

/**
 * Estatus canónico del header, con el MISMO fallback por `status_id` que usa
 * `headerStatusMeta` para pintar el badge.
 *
 * Existe porque no tenerlo causó un bug real: la pantalla mostraba "Finalizado"
 * (el badge cae al `status_id` cuando el `status_name` no matchea) mientras los
 * booleanos de las acciones comparaban el string crudo sin fallback y daban
 * `false`. Resultado: la carga se veía finalizada y el botón de crear manifiesto
 * no aparecía nunca.
 *
 * Regla: cualquier decisión sobre el estatus del header pasa por acá. Si el badge
 * y un botón no coinciden, uno de los dos miente.
 */
export function resolveHeaderStatusName(
  statusName?: string | null,
  statusId?: number | null,
): ImportHeaderStatusName | null {
  if (statusName && HEADER_META[statusName]) {
    return statusName as ImportHeaderStatusName
  }
  if (statusId != null && HEADER_ID_TO_NAME[statusId]) {
    return HEADER_ID_TO_NAME[statusId] as ImportHeaderStatusName
  }
  return null
}

/**
 * Estados CERRADOS de la cabecera: el backend rechaza cualquier modificación sobre
 * ellos ('shipping.ShipmentBulkUploadHeader.2' — "ya fue finalizada o cancelada").
 *
 * Existe porque no tenerlo causó un bug real: una carga en Eliminado seguía
 * mostrando "Reintentar" y "Corregir errores", porque esos botones se decidían
 * solo con los estatus de los DETALLES (que conservan sus 75/77/78 después de
 * eliminar la cabecera) sin mirar la cabecera. El operador apretaba y el backend
 * devolvía un error que no explicaba nada.
 *
 * Regla: sobre una cabecera cerrada la única acción que queda es descargar el
 * archivo original — no se reintenta, no se corrige, no se elimina.
 */
const CLOSED_HEADER_STATUSES = new Set<string>([
  ImportHeaderStatusName.Finalized,
  ImportHeaderStatusName.Cancelled,
  ImportHeaderStatusName.Deleted,
])

/**
 * `true` si la cabecera está en un estado terminal (Finalizado / Cancelado /
 * Eliminado). Un estatus que no se pudo decodificar devuelve `false`: ante la
 * duda no se esconden acciones, se dejan como estaban.
 */
export function isClosedHeaderStatus(
  statusName?: string | null,
  statusId?: number | null,
): boolean {
  const s = resolveHeaderStatusName(statusName, statusId)
  return s != null && CLOSED_HEADER_STATUSES.has(s)
}

export function detailStatusMeta(
  statusName?: string | null,
  statusId?: number | null,
): StatusMeta {
  if (statusName && DETAIL_META[statusName]) return DETAIL_META[statusName]
  if (statusId != null && DETAIL_ID_TO_NAME[statusId]) {
    return DETAIL_META[DETAIL_ID_TO_NAME[statusId]]
  }
  return UNKNOWN_META
}

/**
 * Resuelve el nombre canónico del estado de detalle con el mismo fallback name→id
 * que usa `detailStatusMeta`. Necesario para decidir acciones (ver guía / ver payload /
 * eliminar) y contar ejecutados/errores: comparar el `status_name` crudo contra el enum
 * falla cuando el backend manda un name distinto y solo coincide por `status_id`.
 */
export function resolveDetailStatusName(
  statusName?: string | null,
  statusId?: number | null,
): ImportDetailStatusName | null {
  if (statusName && DETAIL_META[statusName]) {
    return statusName as ImportDetailStatusName
  }
  if (statusId != null && DETAIL_ID_TO_NAME[statusId]) {
    return DETAIL_ID_TO_NAME[statusId] as ImportDetailStatusName
  }
  return null
}

// ---------------------------------------------------------------------------
// Reglas de acciones de carga masiva (alineadas al backend)
//
// Los botones se calculan agregando los `status_id` de los detalles (la spec exige
// que el front no habilite lo que el backend rechazaría). Estatus de detalle:
//   73 Pendiente · 74 Ejecutando/OK · 75 Error · 76 Sustituida · 77 Error procesando ·
//   78 Error servidor · 79 En ejecución (procesándose ahora)
// ---------------------------------------------------------------------------

/** Tope de reintentos del backend para errores de servidor (78); se muestra "intento N/40". */
export const SERVER_ERROR_RETRY_CAP = 40

/**
 * Números de guía TEALCA de los detalles que efectivamente crearon una guía.
 *
 * Es la ÚNICA definición del universo de guías de una carga masiva: la usan el
 * botón de crear manifiesto, el de imprimir etiquetas, el conteo del aviso de
 * cobertura y la preselección del modal de despacho.
 *
 * Ojo con la diferencia contra `getBulkActionFlags().executedCount`: ese cuenta
 * FILAS en estado Executed, incluidas las que por lo que sea no tienen
 * `shipment_number`. Acá se necesita el número, no la fila: una guía sin número
 * no se puede preseleccionar, ni incluir, ni imprimir. Si se usara
 * `executedCount` como universo, el aviso "vas a incluir N de M" mentiría.
 */
export function executedGuideNumbers(
  details: Array<{
    status_name?: string | null
    status_id?: number | null
    shipment_number?: string | null
  }>,
): string[] {
  if (!Array.isArray(details)) return []

  return details
    .filter(
      (d) =>
        resolveDetailStatusName(d?.status_name, d?.status_id) ===
        ImportDetailStatusName.Executed,
    )
    .map((d) => d?.shipment_number?.trim() ?? '')
    .filter((n) => n.length > 0)
}

/**
 * Un detalle se puede eliminar salvo cuando ya generó el envío real (74 Ejecutando/OK)
 * o está creándolo justo ahora (79 En ejecución): en ambos casos borrarlo dejaría estado
 * inconsistente contra el backend.
 */
export function canDeleteDetailStatus(
  statusName?: string | null,
  statusId?: number | null,
): boolean {
  const s = resolveDetailStatusName(statusName, statusId)
  return (
    s !== ImportDetailStatusName.Executed &&
    s !== ImportDetailStatusName.Executing
  )
}

export interface BulkActionFlags {
  pendingCount: number
  /** Detalles procesándose ahora mismo (79). */
  runningCount: number
  /** Detalles que ya generaron el envío real (74). */
  executedCount: number
  substitutedCount: number
  /** Errores de dato que el usuario corrige (75 + 77). */
  dataErrorCount: number
  /** Errores de servidor reintentables (78). */
  serverErrorCount: number
  hasExecuting: boolean
  /** Hay 75/77 y la cabecera está abierta → habilita "Corregir errores". */
  canEditErrors: boolean
  /** Hay 78, NINGÚN 75/77 y la cabecera está abierta → habilita "Reintentar". Mutuamente excluyente con canEditErrors. */
  canRetry: boolean
  /** Cabecera 70 (siempre) o 68 sin ningún 74. */
  canDeleteHeader: boolean
  /**
   * Cabecera en estado terminal (69 Finalizado / 71 Cancelado / 72 Eliminado).
   * Las acciones sobre errores no solo se deshabilitan: no se muestran, porque
   * sobre una carga cerrada nunca van a volver a aplicar.
   */
  isHeaderClosed: boolean
}

/**
 * Deriva el estado de los botones de la carga masiva desde los estatus de sus detalles
 * más el estatus de la cabecera. Puro y testeable (las tablas de verdad de la spec son
 * los casos de prueba). Resuelve cada detalle con el mismo fallback name→id del resto.
 */
export function getBulkActionFlags(
  details: Array<{ status_name?: string | null; status_id?: number | null }>,
  headerStatusName?: string | null,
  headerStatusId?: number | null,
): BulkActionFlags {
  let pendingCount = 0
  let runningCount = 0
  let executedCount = 0
  let substitutedCount = 0
  let dataErrorCount = 0
  let serverErrorCount = 0

  for (const d of details) {
    switch (resolveDetailStatusName(d.status_name, d.status_id)) {
      case ImportDetailStatusName.Pending:
        pendingCount++
        break
      case ImportDetailStatusName.Executing:
        runningCount++
        break
      case ImportDetailStatusName.Executed:
        executedCount++
        break
      case ImportDetailStatusName.Substituted:
        substitutedCount++
        break
      case ImportDetailStatusName.Error:
      case ImportDetailStatusName.ErrorProcessing:
        dataErrorCount++
        break
      case ImportDetailStatusName.ErrorServer:
        serverErrorCount++
        break
      default:
        break
    }
  }

  const hasExecuting = executedCount > 0

  // Estatus de cabecera con el mismo fallback name→id que headerStatusMeta.
  const headerName =
    (headerStatusName && HEADER_META[headerStatusName]
      ? headerStatusName
      : headerStatusId != null
        ? HEADER_ID_TO_NAME[headerStatusId]
        : null) ?? null
  const isInProcess = headerName === ImportHeaderStatusName.InProcess
  const isFinalizedWithErrors =
    headerName === ImportHeaderStatusName.FinalizedWithErrors
  // Cabecera cerrada (69/71/72): los detalles conservan sus errores, pero el
  // backend ya no acepta ni reintento ni corrección sobre esta carga.
  const isHeaderClosed = headerName != null && CLOSED_HEADER_STATUSES.has(headerName)

  return {
    pendingCount,
    runningCount,
    executedCount,
    substitutedCount,
    dataErrorCount,
    serverErrorCount,
    hasExecuting,
    canEditErrors: dataErrorCount > 0 && !isHeaderClosed,
    canRetry: serverErrorCount > 0 && dataErrorCount === 0 && !isHeaderClosed,
    canDeleteHeader:
      isFinalizedWithErrors ||
      (isInProcess && !hasExecuting && runningCount === 0),
    isHeaderClosed,
  }
}

// ---------------------------------------------------------------------------
// Mapeo de códigos de error → mensajes UX (Frontend Spec sección 7)
// ---------------------------------------------------------------------------

export const IMPORT_ERROR_MESSAGES: Record<string, string> = {
  'shipping.ShipmentBulkUploadHeader.1':
    'La carga masiva no existe o fue eliminada',
  'shipping.ShipmentBulkUploadHeader.2':
    'Esta carga masiva ya fue finalizada o cancelada, no se puede modificar',
  'shipping.ShipmentBulkUploadHeader.3':
    'Ya existe una carga masiva con ese número',
  'shipping.ShipmentBulkUploadHeader.4':
    'La carga masiva debe tener al menos una guía',
  'shipping.ShipmentBulkUploadHeader.5':
    'No se pudo guardar el archivo en la nube',
  'shipping.ShipmentBulkUploadHeader.6':
    'Esta carga masiva no tiene archivo asociado',
  'shipping.ShipmentBulkUploadHeader.7':
    'Debes subir un archivo o indicar el motivo de la carga masiva',
  'shipping.ShipmentBulkUploadHeader.8':
    'El archivo supera los 25 MB permitidos',
  'shipping.ShipmentBulkUploadHeader.9':
    'El sistema de procesamiento no está disponible. Reintentá en unos minutos.',
  'shipping.ShipmentBulkUploadHeader.10':
    'Tipo de archivo no soportado. Subí CSV, Excel o JSON.',
  'shipping.ShipmentBulkUploadHeader.11':
    'No tenés permisos para acceder a esta carga masiva',
  'shipping.ShipmentBulkUploadHeader.12':
    'La carga masiva debe referenciar un sitio de cuenta o una cuenta',
  'shipping.ShipmentBulkUploadDetail.1': 'El detalle no existe',
  'shipping.ShipmentBulkUploadDetail.2':
    'Esta guía ya fue procesada y no se puede modificar',
  // Vincular carga masiva ↔ manifiesto. El manifiesto se crea en el API .NET y
  // Django valida que exista antes de guardar el vínculo; si no lo encuentra, el
  // manifiesto YA está creado del otro lado y lo único que falta es el vínculo.
  'logistics.MasterShipmentHeader.1':
    'El manifiesto no se encontró del lado del sistema de cargas masivas. El manifiesto sí se creó: reintentá vincularlo.',
  'general.General.5': 'Hay datos inválidos. Revisá los campos marcados.',
}

/** Mensaje UX para un code del envelope; cae al fallback si no está mapeado. */
export function messageForCode(
  code?: string | null,
  fallback = 'Ocurrió un error. Intentá de nuevo.',
): string {
  if (code && IMPORT_ERROR_MESSAGES[code]) return IMPORT_ERROR_MESSAGES[code]
  return fallback
}

// ---------------------------------------------------------------------------
// error_data → errores por campo/fila (general.General.5: validación pydantic)
//
// El backend devuelve claves tipo "shipment_list.0.consignee.phone". Las traducimos
// al índice de grupo + la key de ShipmentGroupErrors para pintar el campo exacto en
// el Review en lugar de un modal genérico. Mapeo conservador: lo que no reconocemos
// se devuelve en `unmapped` para mostrarlo agregado (no se pierde información).
// ---------------------------------------------------------------------------

/** path del backend (luego de `shipment_list.<i>.`) → key de ShipmentGroupErrors. */
const SERVER_FIELD_TO_GROUP_KEY: Record<string, string> = {
  'consignee.phone': 'consigneePhone',
  'consignee.identification': 'consigneeDocNumber',
  'consignee.name': 'consigneeName',
  'consignee.email': 'consigneeEmail',
  'destination.address.line': 'destinationAddress',
  'destination.location': 'destinationCity',
  'destination.store': 'storeCode',
  partner_shipment_id: 'shipmentHeaderReference',
  'shipper.name': 'shipperName',
  'shipper.identification': 'shipperDocNumber',
  // shipper.phone NO se mapea a propósito: viene de user.client y no es editable
  // por fila, así que cae en `unmapped` para evitar un error fantasma que bloquee
  // el envío sin un campo donde corregirlo.
}

/** Traduce el mensaje crudo del backend a algo legible en español. */
function translateServerFieldMessage(path: string, rawMsg: string): string {
  const cleaned = (rawMsg || '').replace(/^Value error,\s*/i, '').trim()
  if (path.endsWith('.phone')) {
    const phone = /(\+\d{1,3} \d+)/.exec(rawMsg)?.[1] || ''
    return phone
      ? `Teléfono inválido (${phone}). Debe tener entre 9 y 15 dígitos.`
      : 'Teléfono inválido. Debe tener entre 9 y 15 dígitos.'
  }
  return cleaned || 'Dato inválido'
}

/**
 * Errores de EJECUCIÓN, los que trae `error_message` de cada detalle.
 *
 * Ojo con la diferencia contra `mapServerFieldErrors`: aquel traduce el
 * `error_data` que devuelve el POST/PUT al ENVIAR (validación de esquema, con
 * rutas tipo `shipment_list.0.consignee.phone`). Este traduce el mensaje que el
 * backend guardó cuando INTENTÓ crear la guía y falló contra una regla de
 * negocio. Llegan por caminos distintos y con formatos distintos.
 *
 * El formato es `<code>: <mensaje en prosa>`, ej.:
 *
 *   shipping.ShipmentHeader.37: El valor declarado ($15.0) de la guía 0 no puede
 *   ser menor a $53.00 para el peso total facturado 1.0
 *
 * Sin traducir, ese texto solo aparece en la tabla del detalle: el operador entra
 * a Corregir errores y ve 10 guías sin saber cuál falló ni por qué. Traducirlo a
 * un error POR CAMPO lo pone en la sección que corresponde, en la guía que
 * corresponde.
 */

/** Extrae el primer monto en dólares del mensaje, ej. "$53.00" → "$53.00". */
const firstAmount = (msg: string): string | null => {
  // El mensaje trae DOS montos: el declarado y el mínimo. Se busca el que sigue a
  // "menor a", no el primero de la oración, que es el valor que puso el operador.
  const afterMenorA = /menor a\s*\$?\s*([\d.,]+)/i.exec(msg)
  if (afterMenorA) return `$${afterMenorA[1]}`
  return null
}

/**
 * Datos de la guía que la traducción necesita para decidir QUÉ campo marcar.
 *
 * Varios mensajes del backend nombran el valor ofensor pero no el campo: el mismo
 * `account.Account.1` puede venir por el nombre del remitente o el del destinatario.
 * Con el grupo a mano se compara el valor listado contra cada campo y se marca el
 * que corresponde, en vez de pintar los dos y mandar al operador a adivinar.
 */
export interface ExecutionErrorContext {
  shipperName?: string
  consigneeName?: string
  /** Solo los dígitos locales, sin el `+58` (como los guarda el editor). */
  consigneePhone?: string
  shipmentHeaderReference?: string
  /** Código de localidad destino (`destination.location` del payload). */
  destinationCity?: string
  /** buCode de la tienda destino (`destination.store` del payload). */
  storeCode?: string
  /**
   * En COD la sección Remitente ni se renderiza (sale de la cuenta). Marcar un
   * campo del remitente ahí dejaría la guía bloqueada sin nada que editar, así
   * que esos errores caen al aviso de la fila.
   */
  isCOD?: boolean
}

/**
 * Pesos del mensaje de `shipping.ShipmentHeader.35`, ej.:
 *   "El peso total facturado (5454.818181818182 kg) no puede ser mayor a 999.999 kg"
 * → `{ total: '5454.82', max: '999.999' }`.
 */
const weightsFromMessage = (
  msg: string
): { total: string | null; max: string | null } => {
  const rawTotal = /facturado\s*\(?\s*([\d.,]+)\s*kg/i.exec(msg)?.[1] ?? null
  const max = /mayor a\s*([\d.,]+)\s*kg/i.exec(msg)?.[1] ?? null
  // El backend manda el total con la precisión completa del float
  // (5454.818181818182): se recorta a 2 decimales, que es como el operador ve los
  // pesos en la tabla de piezas. El máximo se deja tal cual — es un umbral, no una
  // medición, y redondearlo cambiaría el límite que se le está comunicando.
  const parsed = rawTotal === null ? NaN : Number(rawTotal.replace(/,/g, ''))
  const total = Number.isFinite(parsed)
    ? String(Math.round(parsed * 100) / 100)
    : rawTotal
  return { total, max }
}

interface ExecutionErrorRule {
  /** Código del backend, si lo trae. */
  code?: string
  /** Respaldo por texto, para cuando el código cambia o no viene. */
  test: RegExp
  /**
   * Errores por campo que produce la regla. Devuelve `{}` cuando reconoce el
   * mensaje pero no puede anclarlo a un campo editable de ESTA guía (ej. el
   * teléfono rechazado es el del remitente, que no se edita por fila): así el
   * mensaje crudo queda en el aviso de la fila en vez de marcar el campo
   * equivocado y bloquear el envío sin dónde corregir.
   */
  toErrors: (raw: string, ctx: ExecutionErrorContext) => Record<string, string>
  /**
   * ¿El dato que el backend rechazó YA NO está en la guía del ctx? Solo para
   * reglas que pueden verificarlo comparando el valor listado en el mensaje
   * contra la guía actual. `true` = verificado que se corrigió; `false` o
   * ausente = no se puede asegurar (y el error se sigue mostrando).
   */
  isResolved?: (raw: string, ctx: ExecutionErrorContext) => boolean
}

/** Normaliza para comparar valores del mensaje contra los campos de la guía. */
const normValue = (v?: string | null): string =>
  (v ?? '').replace(/\s+/g, ' ').trim().toUpperCase()

/**
 * Valores que el backend listó como inválidos. Van después de los dos puntos del
 * cuerpo ("... no son válidos: A, B") y la oración de la regla puede seguir tras
 * un punto, así que se corta ahí.
 */
const listedValues = (body: string): string[] => {
  const idx = body.indexOf(':')
  if (idx === -1) return []
  const tail = body.slice(idx + 1)
  // El punto cierra la enumeración… salvo dentro de un número ("$53.00") o de un
  // par entre paréntesis, que el backend usa en account.AccountSite.2.
  const stop = /\.(?:\s|$)/.exec(tail)
  const listPart = stop ? tail.slice(0, stop.index) : tail
  return listPart
    .split(/,(?![^(]*\))/)
    .map((v) => v.trim())
    .filter(Boolean)
}

/**
 * IDs de guía aliada listados en los pares de `account.AccountSite.2`, ej.:
 *   "... ya existen: ('2500-1', 'LSP13422730-C-1'), ('2500-1', 'LSP99')"
 * → ['LSP13422730-C-1', 'LSP99'] (normalizados). El primer valor del par es la
 * línea de facturación; el duplicado que le importa a la fila es el segundo.
 */
const listedPairReferences = (body: string): string[] => {
  const out: string[] = []
  const re = /\(\s*'([^']*)'\s*,\s*'([^']*)'\s*\)/g
  let m: RegExpExecArray | null = re.exec(body)
  while (m) {
    const ref = normValue(m[2])
    if (ref) out.push(ref)
    m = re.exec(body)
  }
  return out
}

/**
 * ¿El nº de guía aliada de la guía actual sigue siendo uno de los que el backend
 * listó como duplicados?
 *
 * Es el chequeo del round-trip por Excel: el operador baja las guías con error,
 * CAMBIA el número duplicado en el archivo y lo vuelve a subir. El
 * `error_message` guardado en el detalle sigue nombrando el número VIEJO; sin
 * comparar contra la guía corregida, la fila volvía al editor marcada en rojo y
 * bloqueada por un duplicado que ya no existe.
 *
 * Sin nº actual en el ctx o sin pares parseables no se puede verificar nada: se
 * asume vigente (mejor un aviso de más que dejar pasar un duplicado real).
 */
const pairStillOffending = (
  raw: string,
  ctx: ExecutionErrorContext,
): boolean => {
  const current = normValue(ctx.shipmentHeaderReference)
  if (!current) return true
  const listed = listedPairReferences(raw)
  if (listed.length === 0) return true
  return listed.indexOf(current) !== -1
}

/**
 * Errores por campo para las reglas de NOMBRE de cliente (charset y cantidad de
 * palabras). El mensaje no dice si el ofensor es el remitente o el destinatario:
 * se compara cada nombre listado contra los dos campos, con el validador de la
 * regla como respaldo, y se marca solo el/los que SIGUEN ofendiendo — así el
 * campo que el operador ya corrigió no vuelve marcado.
 */
const nameRuleErrors = (
  raw: string,
  ctx: ExecutionErrorContext,
  validator: (name?: string | null) => string | null,
  fallbackMsg: string,
): Record<string, string> => {
  const listed = listedValues(raw).map(normValue).filter(Boolean)
  const hits = (name?: string): boolean =>
    !!name?.trim() &&
    (listed.indexOf(normValue(name)) !== -1 || !!validator(name))
  const out: Record<string, string> = {}
  // El texto sale del validador compartido: el operador ve el MISMO mensaje
  // que le habría salido tipeando ese nombre a mano en el editor.
  if (!ctx.isCOD && hits(ctx.shipperName))
    out.shipperName = validator(ctx.shipperName) || fallbackMsg
  if (hits(ctx.consigneeName))
    out.consigneeName = validator(ctx.consigneeName) || fallbackMsg
  return out
}

/**
 * Resolución verificable de las reglas de nombre: hay al menos un nombre en el
 * ctx para comparar y NINGUNO sigue ofendiendo (ni por valor listado ni por
 * validador). A diferencia de `nameRuleErrors`, acá se chequea el remitente
 * también en COD: no es editable por fila, pero si sigue ofendiendo el backend
 * va a rechazar de nuevo y la nota del rechazo no debe apagarse.
 */
const nameRuleResolved = (
  raw: string,
  ctx: ExecutionErrorContext,
  validator: (name?: string | null) => string | null,
): boolean => {
  const listed = listedValues(raw).map(normValue).filter(Boolean)
  const offends = (name?: string): boolean =>
    !!name?.trim() &&
    (listed.indexOf(normValue(name)) !== -1 || !!validator(name))
  if (!(ctx.shipperName ?? '').trim() && !(ctx.consigneeName ?? '').trim())
    return false
  return !offends(ctx.shipperName) && !offends(ctx.consigneeName)
}

/**
 * Campo de destino cuyo valor ACTUAL sigue siendo una de las localidades que el
 * backend listó en `business.BusinessUnit.9`, o `null` si ninguno coincide (el
 * operador ya cambió el destino) o no hay nada que comparar.
 *
 * Se chequean los dos códigos que viajan en el payload: `destination.location`
 * (= `destinationCity`) y `destination.store` (= `storeCode`) — el código
 * listado puede ser cualquiera de los dos según el tipo de entrega.
 */
const offendingDestination = (
  raw: string,
  ctx: ExecutionErrorContext,
): 'destinationCity' | 'storeCode' | null => {
  const listed = listedValues(raw).map(normValue).filter(Boolean)
  if (listed.length === 0) return null
  const city = normValue(ctx.destinationCity)
  if (city && listed.indexOf(city) !== -1) return 'destinationCity'
  const store = normValue(ctx.storeCode)
  if (store && listed.indexOf(store) !== -1) return 'storeCode'
  return null
}

const EXECUTION_ERROR_RULES: ExecutionErrorRule[] = [
  {
    // Valor declarado por debajo del mínimo para el peso facturado.
    code: 'shipping.ShipmentHeader.37',
    // `[\s\S]*?` y no `.*`: sin el flag `s` (no compila bajo este target de TS) el
    // punto no cruza saltos de línea, y el backend a veces los mete.
    test: /valor declarado[\s\S]*?(no puede ser menor|menor a)/i,
    // Se muestra en "Piezas": el valor declarado se edita ahí, no en la cabecera.
    toErrors: (raw) => {
      const min = firstAmount(raw)
      return {
        pieces: min
          ? `El total del valor declarado no puede ser menor a ${min}`
          : 'El total del valor declarado es menor al mínimo permitido',
      }
    },
  },
  {
    /**
     * Peso total facturado por encima del tope del servicio, ej.:
     *   shipping.ShipmentHeader.35: El peso total facturado (5454.818181818182 kg)
     *   no puede ser mayor a 999.999 kg para el servicio 69
     *
     * Va a "Piezas" por la misma razón que el valor declarado: el total sale de la
     * suma de las piezas, así que ahí es donde el operador lo baja. Mencionar el
     * total actual importa — con 5454 kg contra un tope de 999 el problema no es
     * ajustar una pieza, es que el archivo traía el peso en la unidad equivocada.
     */
    code: 'shipping.ShipmentHeader.35',
    test: /peso total facturado[\s\S]*?(no puede ser mayor|mayor a)/i,
    toErrors: (raw) => {
      const { total, max } = weightsFromMessage(raw)
      if (!max)
        return {
          pieces: 'El peso total facturado supera el máximo permitido para este servicio',
        }
      return {
        pieces: total
          ? `El peso total facturado (${total} kg) no puede ser mayor a ${max} kg — revisá el peso de las piezas`
          : `El peso total facturado no puede ser mayor a ${max} kg`,
      }
    },
  },
  {
    /**
     * Nombre de cliente de UNA sola palabra, ej.:
     *   account.Account.2: Los siguientes nombres de cliente no son válidos:
     *   SHEIN. Los nombres de cliente deben tener al menos 2 palabras
     *
     * Va ANTES de account.Account.1: su mensaje también dice "no son válidos",
     * así que en el fallback por texto (mensaje sin código) ganaría la regla del
     * charset y el operador vería el mensaje equivocado.
     */
    code: 'account.Account.2',
    test: /nombres? de cliente[\s\S]*?al menos 2 palabras/i,
    toErrors: (raw, ctx) =>
      nameRuleErrors(
        raw,
        ctx,
        clientNameWordCountError,
        'El nombre debe tener al menos 2 palabras (ej. nombre y apellido)',
      ),
    isResolved: (raw, ctx) =>
      nameRuleResolved(raw, ctx, clientNameWordCountError),
  },
  {
    /**
     * Nombre de cliente con caracteres fuera del charset del backend, ej.:
     *   account.Account.1: Los siguientes nombres de cliente no son válidos:
     *   LATIN TRAVEL ESPAÑA. Los nombres de cliente solo pueden contener letras,
     *   números, guiones, puntos, comas, apóstrofes y &
     *
     * El mensaje no dice si es el remitente o el destinatario: se resuelve
     * comparando los nombres listados contra los dos campos de la guía, con el
     * charset como respaldo (el mismo validador que corre en vivo en el editor).
     */
    code: 'account.Account.1',
    test: /nombres? de cliente[\s\S]*?(no (son|es) válidos?|solo pueden contener)/i,
    toErrors: (raw, ctx) =>
      nameRuleErrors(raw, ctx, clientNameError, 'Nombre de cliente no válido'),
    isResolved: (raw, ctx) => nameRuleResolved(raw, ctx, clientNameError),
  },
  {
    /**
     * Nº de guía aliada ya usado en esa línea de facturación, ej.:
     *   account.AccountSite.2: Los siguientes pares de línea de facturación y ID
     *   de guía aliada ya existen: ('2500-1', 'LSP13422730-C-1')
     *
     * Es el duplicado contra la BASE (no contra el archivo). Se reusa el texto que
     * la fila ya muestra cuando `isDuplicateInDB` lo detecta antes de enviar, para
     * que el mismo problema no tenga dos redacciones.
     */
    code: 'account.AccountSite.2',
    test: /(línea de facturación|linea de facturacion)[\s\S]*?ya existen?/i,
    // Ctx-aware: si el nº de guía aliada actual ya no es el listado en el
    // mensaje (el operador lo cambió en el Excel), no se re-marca el campo.
    toErrors: (raw, ctx): Record<string, string> => {
      if (!pairStillOffending(raw, ctx)) return {}
      return {
        shipmentHeaderReference:
          'Esta guía ya existe en el sistema. Edita el número antes de enviar.',
      }
    },
    isResolved: (raw, ctx) => !pairStillOffending(raw, ctx),
  },
  {
    /**
     * Teléfono rechazado por el backend, ej.:
     *   account.PhoneNumber.1: Los siguientes números de teléfono no son válidos:
     *   +58 05841286648
     *
     * Solo se ancla si el número listado es el del DESTINATARIO: el del remitente
     * sale de la cuenta y no se edita por fila, así que marcarlo dejaría la guía
     * bloqueada sin campo donde corregirla (mismo criterio que
     * `SERVER_FIELD_TO_GROUP_KEY`, que omite `shipper.phone` a propósito).
     */
    code: 'account.PhoneNumber.1',
    test: /números? de teléfono[\s\S]*?no (son|es) válidos?/i,
    toErrors: (raw, ctx): Record<string, string> => {
      const digitsOf = (v: string): string => v.replace(/\D/g, '')
      // "+58 05841286648" → "05841286648": se descarta el código de país para poder
      // comparar contra `consigneePhone`, que el editor guarda sin él.
      const listed = listedValues(raw).map((v) => {
        const sp = v.indexOf(' ')
        return digitsOf(sp >= 0 ? v.slice(sp + 1) : v)
      })
      const mine = digitsOf(ctx.consigneePhone || '')
      // Sin contexto (no se sabe de quién es el número) se asume el destinatario:
      // es el único teléfono editable por fila.
      const isConsignee =
        !mine || listed.length === 0 || listed.indexOf(mine) !== -1
      if (!isConsignee) return {}
      // Se prefiere el mensaje por REGLA ("Número FIJO debe tener entre 9 y 11
      // dígitos…") al genérico del backend: dice qué corregir, no solo que está mal.
      const specific = consigneePhoneError(ctx.consigneePhone)
      const shown = listed[0] || ''
      return {
        consigneePhone:
          specific ||
          `El sistema rechazó este número${
            shown ? ` (${shown})` : ''
          }. Verificá la operadora y la cantidad de dígitos.`,
      }
    },
  },
  {
    /**
     * Localidad de destino sin tienda activa que la cubra, ej.:
     *   business.BusinessUnit.9: No se encontraron tiendas que cubran las
     *   siguientes localidades: ET2. Razón: Las tiendas asociadas no existen o
     *   no están activas
     *
     * Se marca el campo (ciudad destino o tienda destino) cuyo valor actual
     * SIGUE siendo el código listado. Si con ctx ninguno coincide, el operador
     * ya cambió el destino y no se marca nada — y el error cuenta como resuelto
     * para que la nota del rechazo se apague sola.
     */
    code: 'business.BusinessUnit.9',
    test: /no se encontraron tiendas[\s\S]*?localidades/i,
    toErrors: (raw, ctx): Record<string, string> => {
      const hit = offendingDestination(raw, ctx)
      if (hit === 'destinationCity')
        return {
          destinationCity:
            'Ninguna tienda activa cubre esta localidad. Elegí otra ciudad o localidad de destino.',
        }
      if (hit === 'storeCode')
        return {
          storeCode:
            'Esta tienda no existe o no está activa. Elegí otra tienda destino.',
        }
      return {}
    },
    isResolved: (raw, ctx) => {
      // Sin datos de destino en el ctx, o sin localidades parseables en el
      // mensaje, no se puede verificar nada: la nota se sigue mostrando.
      if (ctx.destinationCity == null && ctx.storeCode == null) return false
      if (listedValues(raw).map(normValue).filter(Boolean).length === 0)
        return false
      return offendingDestination(raw, ctx) === null
    },
  },
]

/**
 * Traduce el `error_message` de un detalle a errores por campo.
 *
 * Devuelve `{}` si no matchea ninguna regla conocida, o si matchea pero el campo
 * no es editable en esta fila: el mensaje crudo sigue visible en la tabla del
 * detalle y en el aviso de la fila, así que no se pierde información — solo no
 * queda anclado a un campo.
 *
 * @param ctx Datos de la guía, para elegir el campo cuando el mensaje nombra el
 * valor ofensor pero no el campo. Opcional: sin él las reglas ambiguas caen a su
 * comportamiento por defecto.
 */
export function mapDetailExecutionError(
  errorMessage?: string | null,
  ctx: ExecutionErrorContext = {},
): Record<string, string> {
  const hit = matchExecutionErrorRule(errorMessage)
  return hit ? hit.rule.toErrors(hit.body, ctx) : {}
}

/** Regla que reconoce el mensaje, con el cuerpo ya separado del código. */
function matchExecutionErrorRule(
  errorMessage?: string | null,
): { rule: ExecutionErrorRule; body: string } | null {
  const raw = (errorMessage || '').trim()
  if (!raw) return null

  // El código viene como prefijo `codigo: mensaje`; puede no venir.
  // `[\s\S]*` en vez de `.` con flag `s`: el target de TS de este proyecto es
  // anterior a ES2018 y el flag `s` no compila.
  const codeMatch = /^([a-z]+(?:\.[A-Za-z]+)*\.\d+)\s*:\s*([\s\S]*)$/.exec(raw)
  const code = codeMatch?.[1]
  const body = codeMatch?.[2]?.trim() || raw

  // Primero por CÓDIGO exacto: varios mensajes comparten frases ("no son
  // válidos" aparece en account.Account.1 y .2) y el regex de una regla puede
  // pescar el mensaje de otra. El texto queda solo como respaldo para mensajes
  // sin código o con un código que ninguna regla declara.
  if (code) {
    for (const rule of EXECUTION_ERROR_RULES) {
      if (rule.code === code) return { rule, body }
    }
  }
  for (const rule of EXECUTION_ERROR_RULES) {
    if (rule.test.test(body) || rule.test.test(raw)) return { rule, body }
  }

  return null
}

/**
 * ¿El `error_message` guardado en el detalle quedó VERIFICABLEMENTE resuelto en
 * la guía del ctx?
 *
 * `true` solo cuando una regla reconoce el mensaje Y puede comparar el valor
 * ofensor contra la guía actual y confirmar que ya no está (hoy: el nº de guía
 * aliada duplicado de `account.AccountSite.2`, que el round-trip por Excel
 * corrige cambiando el número). Mensajes desconocidos, vacíos o no verificables
 * devuelven `false`: ante la duda, el motivo del rechazo se sigue mostrando.
 */
export function isExecutionErrorResolved(
  errorMessage?: string | null,
  ctx: ExecutionErrorContext = {},
): boolean {
  const hit = matchExecutionErrorRule(errorMessage)
  if (!hit || !hit.rule.isResolved) return false
  return hit.rule.isResolved(hit.body, ctx)
}

/**
 * Mínimo de valor declarado que exigió el backend, como NÚMERO.
 *
 * `mapDetailExecutionError` devuelve un texto ya armado; esto devuelve el dato. La
 * diferencia importa: con el número, el editor puede recalcular el mensaje contra
 * la suma real de las piezas mientras el operador escribe ("faltan $3.00") y
 * hacerlo desaparecer solo cuando llega al mínimo. Con el texto congelado del
 * backend, el mensaje se quedaba fijo diciendo un total que ya no era el actual.
 *
 * Devuelve `null` si el mensaje no es el de valor declarado o no trae monto.
 */
export function minDeclaredValueFromExecutionError(
  errorMessage?: string | null,
): number | null {
  const raw = (errorMessage || '').trim()
  if (!raw) return null
  const isDeclaredValueError =
    raw.indexOf('shipping.ShipmentHeader.37') === 0 ||
    /valor declarado[\s\S]*?(no puede ser menor|menor a)/i.test(raw)
  if (!isDeclaredValueError) return null
  // El mensaje trae DOS montos (el declarado y el mínimo): se toma el que sigue a
  // "menor a", igual que `firstAmount`.
  const m = /menor a\s*\$?\s*([\d.,]+)/i.exec(raw)
  if (!m) return null
  // "1,250.75" → 1250.75: la coma es separador de miles en el texto del backend.
  const n = Number(m[1].replace(/,/g, ''))
  return Number.isFinite(n) && n > 0 ? n : null
}

export interface ServerFieldErrors {
  /** índice de grupo → { keyDeShipmentGroupErrors → mensaje } */
  byIndex: Record<number, Record<string, string>>
  /** mensajes que no se pudieron asociar a un campo editable */
  unmapped: string[]
}

/**
 * Convierte el `error_data` del envelope en errores por fila/campo para el Review.
 * Acepta claves "shipment_list.<i>.<path>"; cualquier otra forma cae en `unmapped`.
 */
export function mapServerFieldErrors(
  errorData?: Record<string, any> | null,
): ServerFieldErrors {
  const byIndex: Record<number, Record<string, string>> = {}
  const unmapped: string[] = []
  if (!errorData || typeof errorData !== 'object') return { byIndex, unmapped }

  for (const [rawKey, rawVal] of Object.entries(errorData)) {
    const rawMsg = typeof rawVal === 'string' ? rawVal : JSON.stringify(rawVal)
    const m = /^shipment_list\.(\d+)\.(.+)$/.exec(rawKey)
    if (!m) {
      unmapped.push(rawMsg)
      continue
    }
    const idx = parseInt(m[1], 10)
    const path = m[2]
    const groupKey = SERVER_FIELD_TO_GROUP_KEY[path]
    const message = translateServerFieldMessage(path, rawMsg)
    if (!groupKey) {
      unmapped.push(message)
      continue
    }
    byIndex[idx] = { ...(byIndex[idx] || {}), [groupKey]: message }
  }

  return { byIndex, unmapped }
}

// ---------------------------------------------------------------------------
// Clasificación de errores: transitorio (auto-retry) vs dato (humano)
// ---------------------------------------------------------------------------

export type ImportErrorKind = 'transient' | 'data'

/**
 * Códigos claramente transitorios — se reintentan solos (con cap + backoff aguas arriba).
 * Conservador a propósito: un error desconocido NO es transitorio (se trata como 'data'
 * para evitar loops de reintento contra un backend que falla por una causa real).
 *
 * Nota (dependencia backend abierta): los errores por detalle llegan hoy como string
 * (`error_message`), no como code estructurado. Hasta que el backend exponga un code/categoría
 * por detalle, la clasificación de detalles es match de string y debe quedar conservadora.
 */
const TRANSIENT_CODES = new Set<string>([
  'shipping.ShipmentBulkUploadHeader.9', // cola Redis / procesamiento no disponible
  'shipping.ShipmentBulkUploadHeader.5', // fallo al subir archivo a Spaces
])

const TRANSIENT_MESSAGE_PATTERNS: RegExp[] = [
  /\btimeout\b/i,
  /\btemporarily unavailable\b/i,
  /\bservice unavailable\b/i,
  /\bconnection (refused|reset|error)\b/i,
  /\b50[0234]\b/, // 500/502/503/504
  /\bredis\b/i,
  /\bqueue\b/i,
]

export function classifyImportError(
  code?: string | null,
  message?: string | null,
): ImportErrorKind {
  if (code && TRANSIENT_CODES.has(code)) return 'transient'
  if (message && TRANSIENT_MESSAGE_PATTERNS.some((re) => re.test(message))) {
    return 'transient'
  }
  return 'data'
}
